import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { usePathname, useSearchParams } from 'next/navigation'

import Superbanner from '@/components/Recomendador/Superbanner'
import DevicesSlider from '@/components/DevicesSlider';

import SmartRecomender from '@/components/Recomendador/SmartRecomender'
import BestSellers from '@/components/Recomendador/BestSellers'
import RecomendatorBanner, {
  RECOMENDATOR_BANNER_MODES,
} from '@/components/Recomendador/RecomendatorBanner'
import RecomendedProducts from '@/components/Recomendador/RecomendedProducts'
import SeparatorLine from '@/components/SeparatorLine'
import ShoppingCart900 from '@/components/ShoppingCart/ShoppingCart900'
import { API_STATUS } from '@/utils/constants'

export const Recomendador = ({
  screenType,
}) => {
  // 1) Redux state
  const recomendatorData = useSelector((state) => state.recomendator?.data)
  const recomendedPackagesStatus = useSelector(
    (state) => state.recomendator?.status?.recomendPackages,
  )
  const superbanner = useSelector((state) => state.recomendator?.data?.superbanner)
  const initRecom = useSelector((state) => state.recomendator?.status?.init)
  const recomendedProducts = useSelector((state) => state.recomendator?.recomendedPackages)

  // 2) URL hooks
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // 3) idsToAdd extraídos de superbanner.idToAdd (e.g. "303;304")
  const idsToAdd = useMemo(() => {
    if (!superbanner?.idToAdd) return []
    return String(superbanner.idToAdd)
      .split(';')
      .map((id) => id.trim())
      .filter(Boolean)
  }, [superbanner])

  // 4) Parsear tvChannels existentes
  const tvParam = searchParams.get('tvChannels') || ''
  const tvList = useMemo(
    () => tvParam.split(/[;,]/).map((v) => v.trim()).filter(Boolean),
    [tvParam],
  )

  // 5) shouldShowSuperbanner: false solo si tvList contiene *alguno* de los idsToAdd
const shouldShowSuperbanner = useMemo(() => {
  if (tvList.length === 0) return false

  // ocultar si hay al menos uno de los ids
  const hasAny = idsToAdd.some((id) => tvList.includes(id))

  return !hasAny
}, [tvList, idsToAdd])

  // 6) Reconstruir URL base
  const baseUrl = useMemo(() => {
    const params = new URLSearchParams(searchParams.toString())
    return `${pathname}?${params.toString()}`
  }, [pathname, searchParams])

  // 7) actionUrl: añade solo los idsToAdd que falten
  const actionUrl = useMemo(() => {
    if (!shouldShowSuperbanner) return baseUrl

    const params = new URLSearchParams(searchParams.toString())
    const currentSet = new Set(
      (params.get('tvChannels') || '')
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter(Boolean),
    )

    // añadir solo los que falten
    idsToAdd.forEach((id) => currentSet.add(id))

    params.set('tvChannels', Array.from(currentSet).join(';'))
    return `${pathname}?${params.toString()}`
  }, [shouldShowSuperbanner, baseUrl, pathname, searchParams, idsToAdd])

  // 8) Sobrescribir hrefs en superbanner config
  const bannerConfig = useMemo(() => {
    if (!superbanner) return null
    return {
      ...superbanner,
      ...(superbanner['Button-1'] && {
        'Button-1': {
          ...superbanner['Button-1'],
          href: actionUrl,
        },
      }),
      ...(superbanner['CustomLink-1'] && {
        'CustomLink-1': {
          ...superbanner['CustomLink-1'],
          href: actionUrl,
        },
      }),
    }
  }, [superbanner, actionUrl])

  return (
    <section className="Recomendador">
      {/* Loading / inicial */}
      {!recomendedProducts && (
        <div className="Recomendador_wrapper">
          {initRecom === API_STATUS.OK && recomendedPackagesStatus !== API_STATUS.LOADING && (
            <>
              <SmartRecomender className="container" info={recomendatorData} screenType={screenType} />
              <BestSellers screenType={screenType} />
              <SeparatorLine />
              <div className="Recomendador_wrapper_devicesSlider container">
                <DevicesSlider />
              </div>
              <RecomendatorBanner mode={RECOMENDATOR_BANNER_MODES.BANNER} screenType={screenType} />
            </>
          )}
        </div>
      )}

      {/* Productos recomendados y superbanner */}
      {recomendedProducts && (
        <div className="Recomendador_wrapper">
          <RecomendedProducts screenType={screenType} />

          {/* Solo renderiza Superbanner si shouldShowSuperbanner es true */}
          {shouldShowSuperbanner && bannerConfig && (
            <Superbanner {...bannerConfig} />
          )}
          <div className="container">
            <ShoppingCart900 phone="900 104 854" campaign="900104854-C2C" origin="productosRecomendados"/>
          </div>
          <RecomendatorBanner mode={RECOMENDATOR_BANNER_MODES.ACTIONS} screenType={screenType} />
        </div>
      )}

      <style jsx>
        {`
        .Recomendador {
          gap: 1rem;
          display: flex;
          flex-direction: column;
        }
        .Recomendador_wrapper {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        @media screen and (min-width: 768px) {
          .Recomendador .characsBlock {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}
      </style>
    </section>
  )
}

export default Recomendador;
